import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../firebase';
import { fetchLessonsForDateRange, ensureLessonForOccurrence, lessonIdForOccurrence } from '../../services/lessonService';
// Attendance outcome — the second axis on the session record (card
// fwMubDlBzniA3JQtbUGi, Phase A). Vocabulary, precedence and the admin-only
// rule all live in the service; this page only renders and calls.
import { outcomeChip, outcomeChoices, canChangeOutcome, isUnmarked, UNMARKED_CLOCK, setLessonOutcome, OUTCOME_META } from '../../services/lessonOutcome';
import { fetchStudent, fetchStudents } from '../../services/studentService';
import { fetchAssignedTimeSlots } from '../../services/timeSlotService';
import { fetchAllEvents } from '../../services/eventService';
import { expandOnto, lessonOccurrence, eventOccurrence, ymd } from '../../utils/occurrenceExpansion';
import { nextLessonCountdown } from './todayCountdown';
// The ACTIVE set, the same one families see: an expired or switched-off
// announcement stays on Manage Announcements and leaves this page.
import { fetchActiveAnnouncements as fetchAnnouncementsList, addAnnouncement } from '../../services/announcementService';
import PortalHeader from '../../components/Portal/PortalHeader';
import PortalShellContext from '../../components/Portal/PortalShellContext';
import TestModeBanner from '../../components/TestMode/TestModeBanner';
import AccessNotice from '../../components/Portal/AccessNotice';
import { isPermissionDenied } from '../../utils/permissions';
import { useTestMode } from '../../contexts/TestModeContext';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
// Same released-run reader and the same join the Scheduling calendar uses, so
// the two surfaces can never disagree about who has a lesson.
import { fetchLatestSchedule } from '../../services/teacherViewData';
import { buildRotationLookup, teacherIdForOccurrence, teacherIdFromRecord } from '../../services/rotationLookup';
// The studio's location colour-coding, shared with the Scheduling calendar so
// Lakeville is the same blue in both places. Do not fork these.
import { LOCATIONS } from './Scheduling/locations';
import ViewToggle from '../../components/Portal/ViewToggle';
// Change who teaches a lesson on the day (Anthony, 2026-10-04). The model
// decides which lessons move; the callable writes rotation + records at once.
import { isReassignable, startTimes, defaultFromTime, locationsFrom, defaultTargets, buildMoves, overlapWarnings, applyMovesLocally } from './todayReassign';
import { reassignLessons } from '../../services/lessonReassignService';
// Cancel a lesson ahead of time, as an admin (2026-10-08): the same callable a
// family uses, plus `by: 'cyv'` when the studio is the one cancelling.
import { cancelLesson } from '../../services/makeupService';
// The next lesson as its own card (Today redesign, 2026-10-08).
import TodayUpNext from './TodayUpNext';
// The team's headshots, the same faces the Work cards show (Anthony, 2026-10-07:
// easier to see who is teaching what at a glance).
import { Avatar, uidForTeacherId } from '../../components/Portal/Work/Avatar';

import '../../components/Portal/portalSurface.css';
import './TodayPage.css';

// STRICT resolver. locations.js's own getLocationColor() falls back to
// LOCATIONS[0] for anything it does not recognise, so a null location — an
// event, or a legacy MMS row — would render confidently as Lakeville blue.
// A wrong colour is worse than no colour on a surface whose whole job here is
// telling you where to be, so unknown resolves to null and the row stays plain.
const locationColor = (loc) => LOCATIONS.find((l) => l.value === loc) || null;

// A session record for a SCHEDULED occurrence that was cancelled (family or
// CYV). Only meaningful for a record that joined no occurrence — see the
// legacy half of scheduleRows. An MMS-import row has no `origin` and is never
// one of these.
export const isCancelledOccurrenceRecord = (l) =>
	!!(l && l.origin && l.origin.source === 'timeSlots' && l.origin.slotId && l.origin.originDate
		&& (l.outcome === 'cancelled_family' || l.outcome === 'cancelled_cyv'));

// WHICH TONE GOES WHERE, and it is a measurement, not taste. WCAG against
// white ink, computed 2026-08-30: the `bar` tones FAIL as button fills —
// Lakeville #1d83d4 is 4.00 and Headline #e0699a only 3.15, below AA for a
// button label and the same shape as the 1.21:1 Comfort-Dark bug found earlier
// the same day. The `text` tones PASS: #1565a6 is 6.10 and #b03f78 is 5.49.
// So `text` fills anything carrying a label, and `bar` stays on the accent
// stripe and the dot, where it carries no text at all — which is what it was
// designed for. The chips set BOTH background and colour (5.69 / 5.02 on their
// own fills), which is also why they are theme-independent: the trap is
// setting only one half over a themed surface.

const PHONE_VIEW_KEY = 'cyv.today.phoneView';

const TodayPage = () => {
	const navigate = useNavigate();
	// Inside the portal shell the page title lives in Today's own header card
	// (2026-10-08 redesign); outside it, PortalHeader still brings the chrome.
	const inShell = useContext(PortalShellContext);
	const { testMode } = useTestMode();
	const { userProfile, currentUser } = useAuth();
	// Owner is checked alongside admin for the same reason portalNav.js does it:
	// owner status is independent of role, and the rules treat the two
	// separately, so an isAdmin-only gate would hide this from someone the
	// database still admits.
	const canPostAnnouncement =
		userProfile?.role === 'admin' || userProfile?.owner === true;
	// ── Mine / Full studio (Anthony, 2026-08-30) ────────────────────────────
	//
	// "When I sit down to teach, I can just put my assigned lessons, and I don't
	// have to see the whole entire schedule."
	//
	// DECLARED HERE, AT THE TOP, AND THE POSITION IS LOAD-BEARING. `scope` is
	// read by scheduleRows' dependency array ~200 lines below. Babel transpiles
	// `const` to `var` for this project's browser targets, which removes the
	// temporal dead zone: a read above the declaration yields `undefined`
	// instead of throwing, and a useMemo/useCallback dependency array is
	// evaluated WHERE IT IS WRITTEN. That combination shipped a scheduler
	// control on 2026-08-30 that highlighted correctly and changed nothing,
	// past a fully green suite. Do not move these below scheduleRows.
	//
	// null = not yet resolved (preference still loading). Treated as 'studio'
	// for filtering, because showing everything before we know is safe and
	// hiding a lesson before we know is not.
	const [scope, setScope] = useState(null);
	const scopeTouched = useRef(false);
	// { byEvent, byDayLocation } from the released rotation (each date from the
	// newest released run covering it, fetchLatestSchedule), or null when the
	// read is denied/absent — in which case no row carries a teacher.
	const [rotation, setRotation] = useState(null);
	const [rotationTeachers, setRotationTeachers] = useState({});
	// ── Change a lesson's teacher (Anthony, 2026-10-04) ─────────────────────
	// "Grace and I switched locations halfway through the day today." Admin
	// only, matching the callable; declared up here with the rest of the state
	// (the use-before-declare trap above). activeTeachers is the picker list.
	const canReassign = userProfile?.role === 'admin';
	const [activeTeachers, setActiveTeachers] = useState([]);
	const [openTeacherFor, setOpenTeacherFor] = useState(null);
	const [reassignBusy, setReassignBusy] = useState(false);
	const [reassignError, setReassignError] = useState('');
	const [reassignNote, setReassignNote] = useState('');
	const [showSwap, setShowSwap] = useState(false);
	const [swapFrom, setSwapFrom] = useState('');
	const [swapTargets, setSwapTargets] = useState({});
	const [lessons, setLessons] = useState([]);
	const [announcements, setAnnouncements] = useState([]);
	const [loading, setLoading] = useState(true);
	const [lessonsDenied, setLessonsDenied] = useState(false);
	// PHONE VIEW (Anthony, 2026-09-24): "a toggle up top that is a condensed
	// version so that it's more of an at-a-glance view, and then it can be
	// opened to show all of the cards." Glance = one short row per lesson;
	// Cards = the full phone cards. Phone-only (CSS): the desktop table is
	// already at-a-glance. Remembered per device in localStorage — a viewer
	// convenience, wrapped because storage can throw (private mode, PWA
	// quirks) and the page must render either way. Declared up here with the
	// rest of the state, above every memo (the use-before-declare trap).
	const [phoneView, setPhoneView] = useState(() => {
		try { return window.localStorage.getItem(PHONE_VIEW_KEY) === 'cards' ? 'cards' : 'glance'; } catch { return 'glance'; }
	});
	// Lessons opened into a full card while in Glance.
	const [expandedRows, setExpandedRows] = useState(() => new Set());
	const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
	const [newAnnouncement, setNewAnnouncement] = useState('');
	const [currentTime, setCurrentTime] = useState(new Date());

	// ── Date navigation (card rxiYUa16MCMiVIgHyuS9) ──────────────────────────
	//
	// "I want the ability to be able to change the date and view future day
	// schedules... It should just stay the same format and just pull the data for
	// the new day." (Anthony, 2026-08-12.)
	//
	// THE DATA SOURCE HAD TO CHANGE FOR THAT TO MEAN ANYTHING. This page read only
	// the legacy MMS `lessons` collection, which holds the 2025-26 import plus a
	// dozen "Test Student" rows and NOTHING from Aug 2026 on — so it showed zero
	// lessons today and would have shown zero for every future date. The real fall
	// schedule lives in `timeSlots`. A date picker over `lessons` alone would have
	// been a date picker over an empty collection.
	//
	// Both sources are merged rather than swapped, so last season's history and
	// the Start button that depends on a real `lessons` doc are not lost.
	//
	// THE "NOTHING FROM AUG 2026 ON" CLAUSE ABOVE WAS TRUE WHEN WRITTEN AND IS
	// NOT TRUE NOW — `ensureLessonForOccurrence` writes current-season session
	// records into that same collection. The merge below therefore JOINS the two
	// halves on the record's `origin` rather than concatenating them; see
	// scheduleRows. Left standing, the concatenation showed a student twice as
	// soon as their lesson had been opened.
	const [anchor, setAnchor] = useState(() => ymd(new Date()));
	const [slots, setSlots] = useState([]);
	const [events, setEvents] = useState([]);
	const [studentsById, setStudentsById] = useState({});
	const isToday = anchor === ymd(currentTime);

	// The recurring sources are loaded ONCE and re-expanded per day, so stepping
	// through dates is instant and costs no extra reads.
	useEffect(() => {
		(async () => {
			const [sl, ev, stu, run] = await Promise.allSettled([
				fetchAssignedTimeSlots(),
				fetchAllEvents(),
				fetchStudents(),
				// Released rotation, for "who is teaching this". allSettled, so a
				// denied viewSchedule costs the teacher NAMES and the Mine filter,
				// never the day's lessons.
				fetchLatestSchedule(),
			]);
			if (sl.status === 'fulfilled') setSlots(sl.value);
			else if (isPermissionDenied(sl.reason)) setLessonsDenied(true);
			if (ev.status === 'fulfilled') setEvents(ev.value);
			if (run.status === 'fulfilled') {
				setRotation(buildRotationLookup(run.value.assignments));
				setRotationTeachers(run.value.teachersById || {});
				setActiveTeachers((run.value.teachers || []).filter((t) => t.active)
					.sort((a, b) => a.name.localeCompare(b.name)));
			}
			else console.error('Today: rotation failed to load (no teacher attribution):', run.reason);
			if (stu.status === 'fulfilled') {
				setStudentsById(Object.fromEntries((stu.value || []).map((x) => [x.id, x])));
			}
		})();
	}, [testMode]);

	// Resolve the Mine/Studio default: the user's saved choice if they have one,
	// otherwise by role. An admin opens the studio view (Grace and Anthony run
	// the studio and mostly want the whole day); a teacher opens her own. Guarded
	// by scopeTouched so a fast click is never overwritten by a slow read.
	useEffect(() => {
		if (scopeTouched.current || !userProfile) return;
		(async () => {
			let pref = null;
			if (currentUser) {
				try {
					const snap = await getDoc(doc(db, 'users', currentUser.uid));
					pref = snap.exists() ? snap.data().todayScope : null;
				} catch (e) {
					console.error('Error loading Today scope preference:', e);
				}
			}
			if (scopeTouched.current) return;
			const isAdminOrOwner = userProfile.role === 'admin' || userProfile.owner === true;
			setScope(pref === 'mine' || pref === 'studio' ? pref : isAdminOrOwner ? 'studio' : 'mine');
		})();
	}, [userProfile, currentUser]);

	// Persisted per user on the users doc, like calendarWeekStart. The self-update
	// rule is a DENYLIST (role/owner/permissions/familyId/email/studentId are
	// pinned; everything else is the user's own), so a non-admin teacher can save
	// this without any rules change.
	const chooseScope = useCallback(async (v) => {
		scopeTouched.current = true;
		setScope(v);
		if (!currentUser) return;
		try {
			await setDoc(doc(db, 'users', currentUser.uid), { todayScope: v }, { merge: true });
		} catch (e) {
			console.error('Error saving Today scope preference:', e);
		}
	}, [currentUser]);

	useEffect(() => {
		fetchTodaySchedule();
	}, [anchor, testMode]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		fetchAnnouncements();

		// Update clock every second
		const timer = setInterval(() => {
			setCurrentTime(new Date());
		}, 1000);

		return () => clearInterval(timer);
	}, [testMode]); // Refetch when test mode changes

	// 'YYYY-MM-DD' -> a LOCAL-midnight Date. Never `new Date(str)`, which parses
	// as UTC and lands on the previous evening in Central.
	const parseYmd = (str) => { const [y, m, d] = str.split('-').map(Number); return new Date(y, m - 1, d); };
	const shiftDay = (n) => setAnchor((cur) => { const d = parseYmd(cur); d.setDate(d.getDate() + n); return ymd(d); });

	const fetchTodaySchedule = async () => {
		try {
			// The legacy `lessons` half, for the anchored day only. Kept because a
			// row here has a real lesson doc behind it, which is what the Lesson
			// Dashboard (and therefore the Start button) opens.
			const day = parseYmd(anchor);
			const next = new Date(day);
			next.setDate(next.getDate() + 1);

			const lessons = await fetchLessonsForDateRange(anchor, ymd(next));

			const lessonsData = await Promise.all(
				lessons.map(async (lesson) => {
					// Fetch student details - use lesson's studentName as default
					let studentName = lesson.studentName || 'Unknown Student';
					let studentData = null;

					// Try to get more detailed info from students collection if linked
					if (lesson.studentId) {
						try {
							const s = await fetchStudent(lesson.studentId);
							if (s) {
								studentData = s;
								studentName = s.fullName || s.name || `${s.firstName} ${s.lastName}`;
							}
						} catch (err) {
							console.error('Error fetching student:', err);
						}
					}

					return {
						...lesson,
						studentName,
						studentData, // Include full student data for filtering
					};
				})
			);

			// Filter lessons based on test mode and student properties
			const filteredLessons = lessonsData.filter(lesson => {
				// If no student data, include the lesson (can't filter without data)
				if (!lesson.studentData) return true;

				const student = lesson.studentData;

				if (testMode) {
					// In test mode: only show lessons for test students that aren't locked/archived
					return student.isTestStudent === true &&
					       !student.locked &&
					       !student.archived;
				} else {
					// In production mode: exclude lessons for test/locked/archived students
					return !student.isTestStudent &&
					       !student.locked &&
					       !student.archived;
				}
			});

			setLessons(filteredLessons);
		} catch (error) {
			console.error('Error fetching lessons:', error);
			// A schedule-read denial (teacher without viewSchedule) must not read
			// as "No lessons scheduled for today" — the page otherwise looks
			// healthy because announcements still load (#27).
			if (isPermissionDenied(error)) setLessonsDenied(true);
		} finally {
			setLoading(false);
		}
	};

	const fetchAnnouncements = async () => {
		try {
			const announcementsData = await fetchAnnouncementsList();
			setAnnouncements(announcementsData);
		} catch (error) {
			console.error('Error fetching announcements:', error);
		}
	};

	const handleAddAnnouncement = async () => {
		if (!newAnnouncement.trim()) return;

		try {
			await addAnnouncement(
				newAnnouncement,
				auth.currentUser?.email || 'Unknown'
			);

			setNewAnnouncement('');
			setShowAnnouncementModal(false);
			fetchAnnouncements();
		} catch (error) {
			console.error('Error adding announcement:', error);
		}
	};

	// Maps a lesson's relative time to a status + .ps-badge tone.
	//
	// A relative status is only MEANINGFUL for today. On any other date "In
	// Progress" would be a lie, so the whole clock comparison is skipped and the
	// row reports where the day sits instead.
	const getTimeStatus = (lessonTime) => {
		if (!isToday) {
			return anchor > ymd(currentTime)
				? { status: 'upcoming', tone: 'info', text: 'Scheduled' }
				: { status: 'completed', tone: 'neutral', text: 'Past' };
		}
		const now = new Date();
		const [hours, minutes] = lessonTime.split(':');
		const lessonDate = new Date();
		lessonDate.setHours(parseInt(hours), parseInt(minutes), 0);

		const diff = lessonDate - now;
		const minutesDiff = Math.floor(diff / 1000 / 60);

		// "Ended", never "Completed": this is the clock, not the record. A lesson
		// row with nothing marked reads UNMARKED_CLOCK instead (see the chip).
		if (minutesDiff < -45) return { status: 'completed', tone: 'neutral', text: 'Ended' };
		if (minutesDiff <= 0 && minutesDiff > -45) return { status: 'active', tone: 'success', text: 'In Progress' };
		if (minutesDiff <= 15) return { status: 'soon', tone: 'warning', text: 'Starting Soon' };
		return { status: 'upcoming', tone: 'info', text: 'Upcoming' };
	};

	// ── the day's rows: legacy `lessons` + the real `timeSlots` schedule ───────
	//
	// Occurrences come from the SHARED expander (utils/occurrenceExpansion.js) —
	// the same engine the Calendar and the customer schedule render with — so a
	// cancelled or moved lesson reads identically on all three surfaces.
	const minutesBetween = (a2, b2) => {
		const [ah, am] = (a2 || '0:0').split(':').map(Number);
		const [bh, bm] = (b2 || '0:0').split(':').map(Number);
		return (bh * 60 + bm) - (ah * 60 + am);
	};

	const scheduleRows = useCallback(() => {
		const date = parseYmd(anchor);
		const dow = date.getDay();
		const out = [];
		slots.forEach((slot) => expandOnto(slot, date, anchor, dow, lessonOccurrence, out));
		events.forEach((ev) => expandOnto(ev, date, anchor, dow, eventOccurrence, out));

		// THE `lessons` COLLECTION IS NO LONGER ONLY LAST SEASON'S IMPORT, AND
		// TREATING IT AS SUCH RENDERED A STUDENT TWICE. `ensureLessonForOccurrence`
		// (card RC21GWvYXflM556QGUpT) mints a session record into that SAME
		// collection the first time a scheduled lesson is opened — so from that
		// moment the day has both the timeSlots occurrence and its own session
		// record, and concatenating the two halves showed the student once for
		// each. It surfaced on PAST dates because opening a lesson is what marking
		// it complete requires: yesterday's completed lessons are exactly the rows
		// that have a session record. Found 2026-09-01.
		//
		// The join key is the record's `origin`, which is what
		// ensureLessonForOccurrence writes it for. A record whose occurrence no
		// longer expands onto this day — the slot was deleted, or the lesson was
		// moved — matches nothing and still renders as its own row, which is
		// correct: the session really happened.
		const sessionByOccurrence = new Map();
		lessons.forEach((l) => {
			const slotId = l.origin && l.origin.slotId;
			const originDate = l.origin && l.origin.originDate;
			if (slotId && originDate) sessionByOccurrence.set(`${slotId}__${originDate}`, l);
		});
		const joinedSessionIds = new Set();

		const fromSchedule = out
			.filter((o) => {
				// Test-mode filtering mirrors the legacy half: a slot's student decides.
				const stu = o.studentId ? studentsById[o.studentId] : null;
				if (!stu) return !testMode; // events have no student — real only
				return testMode
					? stu.isTestStudent === true && !stu.locked && !stu.archived
					: !stu.isTestStudent && !stu.locked && !stu.archived;
			})
			.map((o) => {
				// The session record already minted for THIS occurrence, if any.
				// Resolving it here is what keeps an occurrence and its own record
				// from rendering as two rows, and it also means Start navigates
				// straight to the existing doc instead of re-running the transaction.
				const occKey = o._kind === 'lesson' && o._slotId && o._originDate
					? `${o._slotId}__${o._originDate}`
					: null;
				const session = (occKey && sessionByOccurrence.get(occKey)) || null;
				const sessionId = session ? session.id : null;
				if (sessionId) joinedSessionIds.add(sessionId);
				return {
					id: o._slotId ? `slot-${o._slotId}-${anchor}` : `ev-${o._eventId}-${anchor}`,
					time: o.startTime,
					duration: minutesBetween(o.startTime, o.endTime) || 45,
					studentName: o._kind === 'lesson' ? o.studentName : (o.title || 'Event'),
					studentId: o.studentId || null,
					instrument: o._kind === 'lesson' ? o.instrument : null,
					lessonType: o._kind === 'lesson' ? (o.isMakeup ? 'Make-up' : 'Private') : 'Event',
					// The occurrence identity, so Start can mint the session record on
					// demand (card RC21GWvYXflM556QGUpT). Lessons only — an event has no
					// session to open.
					slotId: o._kind === 'lesson' ? o._slotId : null,
					originDate: o._kind === 'lesson' ? o._originDate : null,
					isMakeup: !!o.isMakeup,
					location: o.location || null,
					// What kind of row this is, so the Mine filter can act on LESSONS
					// only. An event or a closure affects the studio's day whoever is
					// teaching, so those always show.
					kind: o._kind === 'lesson' ? 'lesson' : 'event',
					// Resolved from the released rotation — per lesson, so a timeslot
					// rule handing one slot to a substitute reads as the substitute.
					// null when the rotation does not know, which is NOT the same as
					// "nobody": the status line below surfaces those rather than
					// letting the Mine filter swallow them silently.
					// A teacher STORED on the record (the nightly stamp, or an admin's
					// correction on Lessons Taught) wins: it is the pay fact.
					teacherId: o._kind === 'lesson'
						? teacherIdFromRecord(session) || teacherIdForOccurrence(rotation, o, anchor)
						: null,
					// Resolved from the join above; null until the record is minted.
					lessonDocId: sessionId,
					// Was the student there — from the joined record; null until marked.
					outcome: session ? session.outcome || null : null,
					// Whether the lesson was registered as started, which is what moves
					// Up next on (todayCountdown.rowIsSettled), never the clock alone.
					startedAt: session ? session.startedAt || null : null,
					completedAt: session ? session.completedAt || null : null,
					status: session ? session.status || null : null,
				};
			});

		// Rows with a real doc id and no occurrence behind them: last season's MMS
		// import, plus any session record whose occurrence no longer expands onto
		// this day. Records already joined onto a schedule row above are dropped
		// here — that is the de-duplication.
		//
		// A CANCELLED OCCURRENCE IS NOT A LESSON, AND RENDERING ITS RECORD AS ONE
		// READ AS A DOUBLE-BOOKING (Anthony, 2026-09-22). `cancelLesson` writes the
		// slot exception AND a session record marked cancelled; the exception stops
		// the occurrence expanding, so the record joins nothing and fell through to
		// this path as an ordinary lesson row — beside the makeup booked into the
		// freed time. Dani Sandgren (family-cancelled 09-22) and Eloise Cassens (the
		// makeup) at 4:30 Lakeville is the case. It still renders, because "this was
		// cancelled" is information a teacher needs, but as its own kind: muted,
		// never counted as a lesson, never startable, never offered for marking.
		const legacy = lessons
			.filter((l) => !joinedSessionIds.has(l.id))
			.map((l) => (isCancelledOccurrenceRecord(l) ? {
				id: l.id,
				time: l.time,
				duration: l.duration || 45,
				studentName: l.studentName,
				studentId: l.studentId || null,
				instrument: l.instrument || null,
				lessonType: 'Cancelled',
				slotId: null,
				originDate: null,
				lessonDocId: l.id,
				outcome: l.outcome,
				location: l.location || null,
				kind: 'cancelled',
				// Attributed like the lesson it replaced, so Mine shows a teacher only
				// the cancellations that were on their own day.
				teacherId: teacherIdFromRecord(l) || teacherIdForOccurrence(rotation, {
					_slotId: l.origin.slotId, _originDate: l.origin.originDate, location: l.location,
				}, anchor),
			} : {
				id: l.id,
				time: l.time,
				duration: l.duration || 45,
				studentName: l.studentName,
				studentId: l.studentId || null,
				instrument: l.instrument || null,
				lessonType: l.lessonType || 'Private',
				slotId: null,
				originDate: null,
				lessonDocId: l.id,
				outcome: l.outcome || null,
				startedAt: l.startedAt || null,
				completedAt: l.completedAt || null,
				status: l.status || null,
				location: l.location || null,
				// Last season's MMS import. It predates the rotation entirely, so it
				// can carry no teacher and is treated as unattributed by Mine.
				kind: 'lesson',
				teacherId: null,
			}));

			// Same start time: the cancelled record sits UNDER whatever replaced it.
			return [...legacy, ...fromSchedule].sort((a2, b2) => (a2.time || '').localeCompare(b2.time || '')
				|| (a2.kind === 'cancelled') - (b2.kind === 'cancelled'));
		}, [anchor, slots, events, lessons, studentsById, testMode, rotation]);

		const allRows = scheduleRows();
		const myTeacherId = userProfile?.teacherId || null;
		// Unresolved preference reads as 'studio': showing everything before we know
		// is safe, hiding a lesson before we know is not.
		const effectiveScope = scope || 'studio';
		const isMine = effectiveScope === 'mine';
		const rows = isMine
			? allRows.filter((r) => r.kind === 'event' || (r.teacherId && r.teacherId === myTeacherId))
			: allRows;
		// Counted so nothing disappears without saying so. A lesson the rotation
		// cannot attribute is the dangerous one — hidden AND unexplained would mean
		// walking past a student — so it gets its own count and its own sentence.
		const lessonRows = allRows.filter((r) => r.kind === 'lesson');
		const hiddenOther = isMine
			? lessonRows.filter((r) => r.teacherId && r.teacherId !== myTeacherId).length
			: 0;
		const hiddenUnattributed = isMine ? lessonRows.filter((r) => !r.teacherId).length : 0;

		// ── Up next (Today redesign, 2026-10-08) ───────────────────────────────
		// The countdown lives ONCE, on the card that answers it, for the scope
		// being looked at: My lessons counts to the teacher's own next lesson,
		// Full studio to the studio's. Until now the same "Next in …" pill sat on
		// BOTH tabs (2026-09-13); Design's pass folded it into one place. The
		// rules (lessons only, "next" = not yet REGISTERED as started, Done rather
		// than absent) are todayCountdown's. Ticks off the one-second clock.
		const myLessonRows = lessonRows.filter((r) => r.teacherId && r.teacherId === myTeacherId);
		const scopeLessonRows = isMine ? myLessonRows : lessonRows;
		const upNext = nextLessonCountdown(scopeLessonRows, currentTime, isToday);
		const showUpNext = !!upNext && !lessonsDenied && scopeLessonRows.length > 0;

		// Opening a scheduled lesson mints its session record if it has none, then
		// navigates. Deliberately NOT allowed for a future date: minting a record for
		// a lesson that has not happened is exactly the orphan row the on-demand
		// choice exists to avoid. A PAST date is allowed — a teacher writing up
		// yesterday's lesson is recording something real.
		const isFuture = anchor > ymd(currentTime);
		const [starting, setStarting] = useState(null);

	// ── Attendance outcome (card fwMubDlBzniA3JQtbUGi, Phase A) ──────────────
	// The Status chip is the control; the choices open as a second table row.
	// `cancelled_cyv` is offered to admins only AND refused on the write for
	// anyone else (canChangeOutcome mirrors the rules clause): gating a control
	// is not gating an action.
	const isAdminOrOwner = userProfile?.role === 'admin' || userProfile?.owner === true;
	const [openOutcomeFor, setOpenOutcomeFor] = useState(null);
	const [outcomeBusy, setOutcomeBusy] = useState(null);
	const [outcomeError, setOutcomeError] = useState('');
	const markOutcome = async (row, value) => {
		if (!canChangeOutcome({ from: row.outcome || null, to: value, isAdmin: isAdminOrOwner })) return;
		setOutcomeBusy(row.id);
		setOutcomeError('');
		try {
			const id = await setLessonOutcome({ row, value, uid: currentUser?.uid, date: anchor });
			// Patch the record locally so the join re-renders without a refetch —
			// fetchTodaySchedule costs one student read per row, and reads are
			// the constraint. A record minted by this tap is added with the
			// `origin` the join keys on, so it joins rather than appearing as a
			// second row.
			setLessons((prev) => {
				const patch = { outcome: value };
				if (prev.some((l) => l.id === id)) return prev.map((l) => (l.id === id ? { ...l, ...patch } : l));
				return [...prev, {
					id, date: anchor, time: row.time, studentId: row.studentId, studentName: row.studentName,
					origin: { source: 'timeSlots', slotId: row.slotId, originDate: row.originDate }, ...patch,
				}];
			});
			setOpenOutcomeFor(null);
		} catch (e) {
			console.error('Error marking lesson outcome:', e);
			setOutcomeError('Could not save that. Try again.');
		} finally {
			setOutcomeBusy(null);
		}
	};

	// ── Cancel a lesson ahead of time (admins; Anthony, 2026-10-08) ─────────
	// "cancel it, build the admin Cancel lesson option", for a Friday lesson
	// the status chip could not touch (a FUTURE date has nothing to mark, by
	// design). A status alone would not cancel anything: the lesson lives on
	// the weekly slot, so it would stay on the family's schedule, their
	// calendar feed and the teacher's day. This goes through cancelLesson,
	// which takes it off all of them:
	//   - Family asked: the family's own path, 24h rule and all (credit or not,
	//     the freed time opens for makeups).
	//   - Studio cancelled: one makeup credit whatever the hour, from the
	//     record the server writes as cancelled_cyv; the time stays closed.
	// Offered for a lesson that has not started: any later date, or today
	// before its start. Once it has started, the status chip is the tool.
	const [openCancelFor, setOpenCancelFor] = useState(null);
	const [cancelBusy, setCancelBusy] = useState(false);
	const [cancelError, setCancelError] = useState('');
	const [cancelNote, setCancelNote] = useState('');
	const cancelRow = async (row, by) => {
		if (cancelBusy || !isAdminOrOwner || !row.slotId || !row.originDate) return;
		setCancelBusy(true);
		setCancelError('');
		setCancelNote('');
		try {
			const res = await cancelLesson(row.slotId, row.originDate, undefined, by);
			const outcome = res && res.outcome;
			if (outcome === 'already_cancelled') {
				setCancelNote(`${row.studentName}'s lesson was already cancelled.`);
			} else {
				// Patch locally (reads are the constraint): the occurrence leaves the
				// slot, and the record the server wrote (or will heal nightly)
				// appears as the quiet Cancelled row in its place.
				setSlots((prev) => prev.flatMap((sl) => {
					if (sl.id !== row.slotId) return [sl];
					if (!sl.isRecurring) return [];
					return [{ ...sl, exceptions: { ...(sl.exceptions || {}), [row.originDate]: { cancelled: true } } }];
				}));
				const recId = `occ_${row.slotId}_${row.originDate}`;
				const rec = {
					id: recId, date: anchor, time: row.time, duration: row.duration,
					studentId: row.studentId, studentName: row.studentName, instrument: row.instrument,
					location: row.location, lessonType: row.lessonType,
					origin: { source: 'timeSlots', slotId: row.slotId, originDate: row.originDate },
					outcome: outcome === 'cyv_cancelled' ? 'cancelled_cyv' : 'cancelled_family',
				};
				setLessons((prev) => [...prev.filter((l) => l.id !== recId), rec]);
				setCancelNote(outcome === 'cyv_cancelled'
					? `${row.studentName}'s lesson is cancelled by the studio. The family gets one makeup credit.`
					: outcome === 'credit_issued'
						? `${row.studentName}'s lesson is cancelled. It was more than 24 hours away, so the family got a makeup credit and the time is open for makeups.`
						: `${row.studentName}'s lesson is cancelled. It was within 24 hours, so no makeup credit.`);
			}
			setOpenCancelFor(null);
			setOpenOutcomeFor(null);
		} catch (e) {
			console.error('Cancel failed:', e);
			setCancelError(`Could not cancel the lesson. Please tell Anthony right away. (${e.message || e.code || 'unknown error'})`);
		} finally {
			setCancelBusy(false);
		}
	};
	const cancelChoices = (lesson) => (
		<div className="today-cancel">
			<div className="today-outcome__choices">
				<span className="today-outcome__lead">
					Cancel {lesson.studentName}'s lesson on {parseYmd(anchor).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}?
				</span>
				<button type="button" className="today-outcome__opt" disabled={cancelBusy} onClick={() => cancelRow(lesson, 'family')}>
					<i className={`fa-sharp fa-solid ${OUTCOME_META.cancelled_family.icon}`} aria-hidden="true" /> Family asked
				</button>
				<button type="button" className="today-outcome__opt" disabled={cancelBusy} onClick={() => cancelRow(lesson, 'cyv')}>
					<i className={`fa-sharp fa-solid ${OUTCOME_META.cancelled_cyv.icon}`} aria-hidden="true" /> Studio cancelled
				</button>
				{openCancelFor === lesson.id && (
					<button type="button" className="today-outcome__opt" onClick={() => setOpenCancelFor(null)}>Close</button>
				)}
			</div>
			<p className="today-teacher__hint">
				Family asked: the 24-hour rule decides the makeup credit, and the time opens for makeups.
				{' '}Studio cancelled: the family always gets one makeup credit, and the time stays closed.
			</p>
			{cancelError && <p className="today-outcome__err">{cancelError}</p>}
		</div>
	);

		// One save path for both controls. All or nothing on the server; on
		// success the rows are patched locally (reads are the constraint, and a
		// refetch costs one student read per row).
		const saveMoves = async (moves) => {
			if (!canReassign || reassignBusy || !moves.length) return false;
			setReassignBusy(true);
			setReassignError('');
			setReassignNote('');
			try {
				const res = await reassignLessons(moves);
				setRotation((cur) => applyMovesLocally({ rotation: cur, lessons: [] }, moves).rotation);
				setLessons((cur) => applyMovesLocally({ rotation: null, lessons: cur }, moves).lessons);
				const n = res && typeof res.changed === 'number' ? res.changed : moves.length;
				const pinned = res && res.taughtByPinned ? res.taughtByPinned.length : 0;
				setReassignNote(`${n} lesson${n === 1 ? '' : 's'} moved.`
					+ (pinned ? ` ${pinned} already had "Taught by" set by hand on Lessons Taught, so ${pinned === 1 ? 'its' : 'their'} credit did not change; fix ${pinned === 1 ? 'it' : 'them'} there.` : ''));
				return true;
			} catch (e) {
				console.error('Reassign failed:', e);
				setReassignError(`Could not change the teacher. Please tell Anthony right away. (${e.message || e.code || 'unknown error'})`);
				return false;
			} finally {
				setReassignBusy(false);
			}
		};
		const reassignOne = async (row, teacherId) => {
			if (!isReassignable(row) || teacherId === row.teacherId) { setOpenTeacherFor(null); return; }
			const ok = await saveMoves([{ slotId: row.slotId, originDate: row.originDate, teacherId }]);
			if (ok) setOpenTeacherFor(null);
		};
		const openSwap = () => {
			const hh = String(currentTime.getHours()).padStart(2, '0');
			const mm = String(currentTime.getMinutes()).padStart(2, '0');
			const from = isToday ? defaultFromTime(allRows, `${hh}:${mm}`) : (startTimes(allRows)[0] || '');
			setSwapFrom(from);
			setSwapTargets(defaultTargets(locationsFrom(allRows, from)));
			setReassignError('');
			setShowSwap(true);
		};
		const changeSwapFrom = (from) => {
			setSwapFrom(from);
			setSwapTargets(defaultTargets(locationsFrom(allRows, from)));
		};
		const swapLocs = showSwap ? locationsFrom(allRows, swapFrom) : [];
		const swapMoves = showSwap ? buildMoves(allRows, swapFrom, swapTargets) : [];
		const swapWarnings = showSwap ? overlapWarnings(allRows, swapMoves) : [];
		const teacherLabel = (id) => (id ? rotationTeachers[id] || 'Unknown teacher' : 'No teacher');
		const locName = (v) => (locationColor(v) ? locationColor(v).name : v);

		const openLesson = async (row) => {
			// A scheduled lesson ALWAYS goes through ensureLessonForOccurrence, even
			// when its record already exists: that is where a not-yet-started
			// record picks up a calendar edit made after it was minted, or the
			// dashboard's timer runs against the old time (2026-10-11). Only a row
			// with no occurrence behind it (last season's import) opens directly.
			if (row.lessonDocId && !row.slotId) { navigate(`/portal/work/lesson/${row.lessonDocId}`); return; }
			if (!row.slotId) return;
			setStarting(row.id);
			try {
				const id = await ensureLessonForOccurrence({
					slotId: row.slotId, originDate: row.originDate, date: anchor,
					startTime: row.time, duration: row.duration, studentId: row.studentId,
					studentName: row.studentName, instrument: row.instrument,
					location: row.location, isMakeup: row.isMakeup,
			});
			navigate(`/portal/work/lesson/${id}`);
		} catch (e) {
			console.error('Error opening lesson:', e);
			setStarting(null);
		}
	};

	const formatTime = (time24) => {
		const [hours, minutes] = time24.split(':');
		const hour = parseInt(hours);
		const ampm = hour >= 12 ? 'PM' : 'AM';
		const hour12 = hour % 12 || 12;
		return `${hour12}:${minutes} ${ampm}`;
	};

	const formatDateTime = () => {
		const dateStr = parseYmd(anchor).toLocaleDateString('en-US', {
			weekday: 'long',
			year: 'numeric',
			month: 'long',
			day: 'numeric'
		});
		// The live clock only belongs on today; on any other date it reads as if
		// that day were in progress.
		if (!isToday) return dateStr;
		const timeStr = currentTime.toLocaleTimeString('en-US', {
			hour: '2-digit',
			minute: '2-digit',
			hour12: true
		});
		// The time in its own span so it can read larger than the date
		// (Anthony, 2026-10-30: "make the time display at the top more visible").
		return (
			<>
				{dateStr} • <span className="today-head__time">{timeStr}</span>
			</>
		);
	};

	// Same format, different day — the whole of the ask. Stepper for scrolling
	// through days, a native date input for jumping, and Today to come home.
	const dateBar = (
		<div className="today-datebar">
			<button type="button" className="ps-btn-icon" aria-label="Previous day" onClick={() => shiftDay(-1)}>
				<i className="fa-sharp fa-solid fa-chevron-left" />
			</button>
			<input
				type="date"
				className="ps-input today-datebar__input"
				value={anchor}
				aria-label="Show schedule for date"
				onChange={(e) => e.target.value && setAnchor(e.target.value)}
			/>
			<button type="button" className="ps-btn-icon" aria-label="Next day" onClick={() => shiftDay(1)}>
				<i className="fa-sharp fa-solid fa-chevron-right" />
			</button>
			<button
				type="button"
				className="btn-cyv-ghost today-datebar__today"
				onClick={() => setAnchor(ymd(new Date()))}
				disabled={isToday}>
				Today
			</button>
		</div>
	);

	// Start / Open, one renderer for the list row AND the Up next card, so the
	// two can never disagree about whether a lesson opens.
	//
	// Start resolves to a session record: an existing lesson doc, or one minted
	// on demand from the occurrence (card RC21GWvYXflM556QGUpT). Disabled only
	// when there is nothing to resolve, or when the date is in the future.
	//
	// A PAST lesson stays OPENABLE (Anthony, 2026-09-06): "we are talking to
	// parents at pick up or remember things later that need to be sent to the
	// student." Past lessons read "Open" rather than "Start", and since the
	// 2026-10-08 redesign they are the NEUTRAL ghost button rather than the
	// location colour: the coloured button is the thing to press NEXT, and a
	// day of finished lessons painted the same colour buried it.
	//
	// THE LOCATION COLOUR ARRIVES AS A CUSTOM PROPERTY + data-loc, NOT AN INLINE
	// BACKGROUND, and the reason is not style. `.btn-cyv-teal` in App.css
	// declares `background: #54ada7 !important`, and React's style prop CANNOT
	// emit !important — so a plain inline colour is set on the element, reads
	// back correctly from element.style, and still renders teal. Measured in a
	// browser 2026-08-30: inline rgb(176,63,120), computed rgb(84,173,167). The
	// paired rule in TodayPage.css carries the !important and the specificity
	// to win. `text` tone, not `bar`: 6.10 / 5.49 against white ink where bar
	// gives 4.00 / 3.15. Applied ONLY when enabled, so a disabled Start keeps
	// the class's own dimmed treatment rather than a colour that looks
	// actionable.
	const renderOpenButton = (lesson, timeStatus, loc, extraClass = '') => {
		const canOpen = !(starting === lesson.id || !(lesson.lessonDocId || lesson.slotId) || isFuture);
		const isPast = timeStatus.status === 'completed';
		const coloured = !!loc && canOpen && !isPast;
		return (
			<button
				type="button"
				className={`${isPast ? 'btn-cyv-ghost today-open-btn' : 'btn-cyv-teal'} today-start-btn${extraClass}`}
				data-guide="today-start"
				data-loc={coloured ? loc.value : undefined}
				style={coloured ? { '--today-start-bg': loc.text } : undefined}
				onClick={() => { if (canOpen) openLesson(lesson); }}
				title={
					lesson.lessonDocId || lesson.slotId
						? (isFuture
							? 'Lessons open on the day — or afterwards'
							: isPast
								? 'Open this lesson — add notes, a recap, or feedback after the fact'
								: 'Open the lesson dashboard')
						: 'No lesson to open for this row'
				}
				disabled={!canOpen}>
				<i className={`fa-sharp fa-solid ${starting === lesson.id ? 'fa-spinner fa-spin' : isPast ? 'fa-book-open' : 'fa-play'}`} aria-hidden="true" />{' '}
				{isPast ? 'Open' : 'Start'}
			</button>
		);
	};

	if (loading) {
		return (
			<>
				<PortalHeader title="Today's Schedule" subtitle="Your lessons and updates for today" />
				<div className="ps-page">
					<div className="ps-spinner" role="status" aria-label="Loading" />
				</div>
			</>
		);
	}

	const remaining = rows.filter((l) => getTimeStatus(l.time).status !== 'completed').length;
	// Lessons a human still owes an answer for. Counted and shown, never
	// presumed attended (Anthony, 2026-09-01) — presumption is how the 08-29
	// balance audit found every wrong number.
	const unmarked = rows.filter((r) => isUnmarked(r, { isToday, isFuture, clockStatus: getTimeStatus(r.time).status })).length;

	// Up next: its own end time, so the card can say "45 min · until 4:15".
	const endTimeOf = (t, dur) => {
		const [h, m] = (t || '0:0').split(':').map(Number);
		const e = h * 60 + m + (dur || 45);
		return `${String(Math.floor(e / 60) % 24).padStart(2, '0')}:${String(e % 60).padStart(2, '0')}`;
	};
	const upNextRow = showUpNext && upNext.state === 'next' ? upNext.row : null;
	const upNextLoc = upNextRow ? locationColor(upNextRow.location) : null;
	const upNextTeacher = upNextRow && upNextRow.teacherId ? rotationTeachers[upNextRow.teacherId] || null : null;

	return (
		<>
			{!inShell && <PortalHeader title={isToday ? 'Today' : 'Schedule'} subtitle={formatDateTime()} />}
			<TestModeBanner />
			<div className="ps-page today-page">
				{/* ── Header card (Today redesign, 2026-10-08) ─────────────────────
				    Title, date, the day controls, the day's numbers and the
				    announcements, in ONE card. The four stat tiles sat at the
				    bottom, below the fold on a full day; the announcements took a
				    whole column that was usually empty. Now both are one strip
				    under the date, always above the fold, and a posted announcement
				    opens as a band in the same card. */}
				<section className="ps-card today-head" aria-label="Day">
					<div className="today-head__top">
						<div className="today-head__title">
							<h1 className="today-head__h1">{isToday ? 'Today' : 'Schedule'}</h1>
							<p className="today-head__date">{formatDateTime()}</p>
						</div>
						{dateBar}
					</div>
					<div className="today-head__strip">
						{/* The day in numbers. "Ended" was dropped in the redesign
						    (total minus left says it); Left is a TODAY number and
						    Unmarked a today-and-past one, so each shows only where it
						    means something. */}
						<div className="today-stats" aria-label="This day in numbers">
							<span className="today-stat">
								<span className="today-stat__num">{rows.length}</span>{' '}
								<span className="today-stat__label">total</span>
							</span>
							{isToday && (
								<span className="today-stat">
									<span className="today-stat__num">{remaining}</span>{' '}
									<span className="today-stat__label">left</span>
								</span>
							)}
							{!isFuture && (
								<span className={`today-stat${unmarked > 0 ? ' today-stat--alert' : ''}`}>
									<span className="today-stat__num">{unmarked}</span>{' '}
									<span className="today-stat__label">unmarked</span>
								</span>
							)}
						</div>
						<div className="today-ann">
							{announcements.length === 0 && (
								<span className="today-ann__empty">
									<i className="fa-sharp fa-solid fa-bullhorn" aria-hidden="true" /> No announcements yet
								</span>
							)}
							{/* POSTING is admin-only; READING is not. This page
							    became reachable by non-admin teachers when Today
							    was added to TeacherDashboard, and an announcement
							    is a studio-wide broadcast rather than a
							    teaching-day tool — not something to hand someone
							    by side effect of giving them their schedule. The
							    rules DO permit isEmployeeOrAdmin() to create one,
							    so this is a deliberate UI narrowing, not a
							    reflection of them; a teacher who needs to post
							    asks an owner. A labelled button, never an icon
							    alone. */}
							{canPostAnnouncement && (
								<button
									type="button"
									className="today-ann__add"
									aria-label="Add announcement"
									onClick={() => setShowAnnouncementModal(true)}>
									<i className="fa-sharp fa-solid fa-plus" aria-hidden="true" /> Add
								</button>
							)}
						</div>
					</div>
					{announcements.length > 0 && (
						<div className="today-announcements__list" aria-label="Announcements">
							{announcements.map((announcement) => (
								<div key={announcement.id} className="today-note">
									<i className="fa-sharp fa-solid fa-bullhorn today-note__icon" aria-hidden="true" />
									<div>
										<small className="today-note__meta">
											<strong>{announcement.createdBy}</strong>
											{' '}&bull;{' '}
											{announcement.createdAt?.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
										</small>
										<p className="today-note__msg">{announcement.message}</p>
									</div>
								</div>
							))}
						</div>
					)}
				</section>

				<div className="today-grid">
					<div className={`today-lessons${phoneView === 'glance' ? ' today-lessons--glance' : ''}`}>
						{/* ── Up next (2026-10-08) ─────────────────────────────────
						    The one thing the page is for, as the largest object on
						    it: the next lesson in the scope being looked at, with its
						    own Start. The same lesson stays in the list below, so the
						    two read from one row and can never disagree. Inside
						    .today-lessons on purpose: the location-coloured Start
						    rule is scoped there. Today only; on another day "next"
						    means nothing. */}
						{showUpNext && (
							<TodayUpNext
								countdown={upNext}
								row={upNextRow}
								loc={upNextLoc}
								teacherName={!isMine ? upNextTeacher : null}
								isMine={isMine}
								timeLabel={upNextRow ? formatTime(upNextRow.time).replace(/ (AM|PM)$/, '') : ''}
								untilLabel={upNextRow ? formatTime(endTimeOf(upNextRow.time, upNextRow.duration)) : ''}
								startButton={upNextRow ? renderOpenButton(upNextRow, getTimeStatus(upNextRow.time), upNextLoc, ' today-next__start') : null}
								onProfile={upNextRow && upNextRow.studentId ? () => navigate(`/portal/work/students/${upNextRow.studentId}`) : null}
							/>
						)}

						<div className="ps-card ps-card--pad0">
							{/* List header: whose lessons, and (admins, Full studio) the
							    day-wide teacher change. Mine / Full studio reuses
							    .ps-tabs/.ps-tab, the proven control from the Scheduling
							    page. Always rendered: never a hover-only affordance. */}
							<div className="today-listhead">
								<div className="ps-tabs today-listhead__tabs" role="tablist" aria-label="Whose lessons" data-guide="today-scope">
									<button type="button" role="tab" aria-selected={isMine}
										className={`ps-tab${isMine ? ' is-active' : ''}`}
										onClick={() => chooseScope('mine')}>
										<span className="today-scope-tab__label"><i className="fa-sharp fa-solid fa-user" /> My lessons</span>
									</button>
									<button type="button" role="tab" aria-selected={!isMine}
										className={`ps-tab${!isMine ? ' is-active' : ''}`}
										onClick={() => chooseScope('studio')}>
										<span className="today-scope-tab__label"><i className="fa-sharp fa-solid fa-users" /> Full studio</span>
									</button>
								</div>
								{/* Change teachers for the rest of the day (admins, Full
								    studio). A real labelled button, never an icon alone.
								    On a phone too: the redesign mockup dropped it there,
								    and an admin's phone is where a mid-day swap happens. */}
								{canReassign && !isMine && allRows.some(isReassignable) && (
									<button type="button" className="btn-cyv-ghost today-reassignbar__btn" onClick={openSwap}>
										<i className="fa-sharp fa-solid fa-arrow-right-arrow-left" aria-hidden="true" /> Change teachers…
									</button>
								)}
								{/* Glance / Cards — phones only (CSS hides it above 640px, where
								    the desktop table is already the at-a-glance view). Words,
								    not icons alone: Annelise and Grace should not have to guess
								    what the two icons mean. */}
								<div className="today-viewbar">
									<ViewToggle
										label="How lessons are shown"
										value={phoneView}
										onChange={(v) => {
											setPhoneView(v);
											setExpandedRows(new Set());
											try { window.localStorage.setItem(PHONE_VIEW_KEY, v); } catch { /* storage unavailable: the choice lasts this visit */ }
										}}
										options={[
											{ key: 'glance', icon: 'fa-list', label: 'Glance' },
											{ key: 'cards', icon: 'fa-rectangle-list', label: 'Cards' },
										]}
									/>
								</div>
							</div>
							{reassignNote && <p className="today-reassignbar__note" role="status">{reassignNote}</p>}
							{cancelNote && <p className="today-reassignbar__note" role="status">{cancelNote}</p>}

							{/* NOTHING DISAPPEARS SILENTLY. Filtering to "mine" hides
							    real lessons, and an unattributed one is the dangerous
							    case — hidden and unexplained would mean walking past a
							    student — so it gets its own sentence. At every width. */}
							{isMine && (hiddenOther > 0 || hiddenUnattributed > 0) && (
								<p className="today-hidden-note">
									{hiddenOther > 0 && `${hiddenOther} lesson${hiddenOther === 1 ? '' : 's'} assigned to someone else hidden.`}
									{hiddenOther > 0 && hiddenUnattributed > 0 && ' '}
									{hiddenUnattributed > 0 && `${hiddenUnattributed} lesson${hiddenUnattributed === 1 ? '' : 's'} not assigned in the released schedule — switch to Full studio to see ${hiddenUnattributed === 1 ? 'it' : 'them'}.`}
								</p>
							)}
							{lessonsDenied ? (
								<AccessNotice what="the schedule" />
							) : rows.length === 0 ? (
								<div className="ps-empty">
									<i className="fa-sharp fa-solid fa-calendar-xmark" />
									{/* "You have none" and "there are none" are different
									    statements, and showing the second when the first
									    is true reads as a broken schedule. */}
									{isMine && !myTeacherId ? (
										<p>Your account isn’t linked to a teaching profile — switch to Full studio, or ask an admin to finish your setup.</p>
									) : isMine && lessonRows.length > 0 ? (
										<p>No lessons assigned to you {isToday ? 'today' : 'this day'} — the studio has {lessonRows.length}.</p>
									) : (
										<p>No lessons scheduled for {isToday ? 'today' : 'this day'}</p>
									)}
								</div>
							) : (
								<div className="ps-table-wrap" style={{ border: 'none', borderRadius: 0 }}>
									<table className="ps-table ps-table--fit">
										<thead>
											<tr>
												<th className="col--time">Time</th>
												<th className="col--student">Student</th>
												<th className="col--where">Where</th>
												<th className="col--status">Status</th>
												<th className="col--actions">Actions</th>
											</tr>
										</thead>
										<tbody>
											{rows.map((lesson) => {
												const timeStatus = getTimeStatus(lesson.time);
												const loc = locationColor(lesson.location);
												const teacherName = lesson.teacherId ? rotationTeachers[lesson.teacherId] || null : null;
												// The outcome outranks the clock once someone has said what happened.
												// A lesson whose time has gone by with nothing marked says so,
												// rather than borrowing the clock's "Ended"/"Past".
												const chip = outcomeChip(
													lesson.outcome,
													isUnmarked(lesson, { isToday, isFuture, clockStatus: timeStatus.status }) ? UNMARKED_CLOCK : timeStatus,
												);
												// A CANCELLED row is markable by an ADMIN (Anthony, 2026-10-08:
												// "Admin should maintain that privilege"). Since 2026-09-22 a
												// family cancellation renders as its own quiet row with a plain
												// status, which left no way to turn "Cancelled · family" into
												// "Cancelled · CYV" (the change that grants the makeup credit,
												// attendanceCreditOnOutcome) or to correct it at all. The row
												// carries its record id, so the save needs nothing new. Teachers
												// keep the plain status: into or out of cancelled_cyv is the
												// admin's in the rules, and a family's own cancel is theirs.
												const canMark = (lesson.kind === 'lesson' || (lesson.kind === 'cancelled' && isAdminOrOwner)) && !isFuture;
												const outcomeOpen = canMark && openOutcomeFor === lesson.id;
												// Not started yet: any later date, or today before its start.
												const canCancel = isAdminOrOwner && lesson.kind === 'lesson' && !!lesson.slotId && !!lesson.originDate
													&& (isFuture || (isToday && (timeStatus.status === 'upcoming' || timeStatus.status === 'soon')));
												const cancelOpen = canCancel && !canMark && openCancelFor === lesson.id;
												// Muted by colour, never hidden: the row still says who,
												// when and where, and its Status names who cancelled.
												const isCancelled = lesson.kind === 'cancelled';
												return (
													<React.Fragment key={lesson.id}>
													<tr className={[isCancelled ? 'today-row--cancelled' : '', expandedRows.has(lesson.id) ? 'is-expanded' : ''].filter(Boolean).join(' ') || undefined}>
														{/* The accent stripe is the fast scan: which room, without
														    reading a word. `bar` tone, carrying no text, so its
														    4.00/3.15 contrast against white is irrelevant here —
														    it is decoration, and that is what the tone is for. */}
														<td className="col--time" style={loc ? { borderLeft: `4px solid ${loc.bar}` } : undefined}>
															<span className="ps-cell-strong">{formatTime(lesson.time)}</span>
															<div className="ps-cell-sub">{lesson.duration || 45} min</div>
														</td>
														<td className="col--student">
															{/* .ps-clip: under fixed layout a cell no longer grows to
															    fit, and unclipped content paints over the next column. */}
															{/* TITLE IS NOT DECORATION HERE. Under .ps-table--fit a cell
															    no longer grows, so a long name ellipsizes — measured at the
															    narrowest two-column width (992px viewport): "Meagan \"Red\"
															    Kenley" is 150px of text in 132px of cell. Without a title the
															    truncated half is unrecoverable, and the portal's primary user
															    has keratoconus. The fit modifier's own contract is that the
															    full value stays one hover or one click away; this is the
															    hover. Not a hover-ONLY affordance — the name is on screen,
															    this only recovers what the ellipsis ate. */}
															<span className="ps-cell-strong ps-clip" title={lesson.studentName}>
																{lesson.studentName}
															</span>
															{/* The TYPE rides here now (2026-10-08): "Private" was on
															    nearly every row, so it is the default and says nothing;
															    anything else (Make-up, Event) is the exception and gets
															    a teal word under the name. A cancelled row's Status
															    already names who cancelled. */}
															{(lesson.instrument || (!isCancelled && lesson.lessonType && lesson.lessonType !== 'Private')) && (
																<div className="ps-cell-sub ps-clip" title={[lesson.instrument, !isCancelled && lesson.lessonType !== 'Private' ? lesson.lessonType : null].filter(Boolean).join(' · ')}>
																	{lesson.instrument}
																	{!isCancelled && lesson.lessonType && lesson.lessonType !== 'Private' && (
																		<span className="today-type">{lesson.instrument ? ' · ' : ''}{lesson.lessonType}</span>
																	)}
																</div>
															)}
														</td>
														<td className="col--where">
															{/* Chip sets BOTH its fill and its ink, so it reads the
															    same in every theme rather than borrowing a surface
															    colour it cannot follow. */}
															{loc ? (
																<span className="ps-badge" style={{ background: loc.fill, color: loc.text }}>
																	<i className="fa-sharp fa-solid fa-location-dot" /> {loc.name}
																</span>
															) : (
																<span className="ps-cell-sub">—</span>
															)}
															{/* Teacher only in Full studio: in My lessons every row is
															    yours, so the badge would be noise on every line. */}
															{!isMine && canReassign && isReassignable(lesson) ? (
																/* Admins: the teacher line IS the control, always shown
																   with its caret (never hover-only), "No teacher" when
																   the schedule does not know. */
																<button
																	type="button"
																	className="ps-cell-sub today-teacher__btn"
																	aria-expanded={openTeacherFor === lesson.id}
																	title="Change who is teaching this lesson"
																	onClick={() => { setReassignError(''); setOpenTeacherFor((cur) => (cur === lesson.id ? null : lesson.id)); }}>
																	{teacherName ? (
																		<Avatar uid={uidForTeacherId(lesson.teacherId)} name={teacherName} size={24} className="today-teacher__face" decorative />
																	) : (
																		<i className="fa-sharp fa-solid fa-chalkboard-user" aria-hidden="true" />
																	)}{' '}
																	<span className="ps-clip" title={teacherName ? `Teaching: ${teacherName}` : 'No teacher scheduled'}>{teacherName || 'No teacher'}</span>{'\u00a0'}
																	<i className="fa-sharp fa-solid fa-chevron-down today-outcome__caret" aria-hidden="true" />
																</button>
															) : !isMine && teacherName && (
																<div className="ps-cell-sub today-teacher__line" title={`Teaching: ${teacherName}`}>
																	<Avatar uid={uidForTeacherId(lesson.teacherId)} name={teacherName} size={24} className="today-teacher__face" decorative />
																	<span className="ps-clip">{teacherName}</span>
																</div>
															)}
														</td>
														<td className="col--status">
															{canMark ? (
																/* The chip IS the control for today and past dates — a
																   real button, always carrying its caret so the affordance
																   is never hover-only. Future dates and events keep
																   the plain chip: nothing has happened to mark. */
																<button
																	type="button"
																	className={`ps-badge ps-badge--${chip.tone} today-outcome__chip`}
																	data-guide="today-outcome"
																	aria-expanded={outcomeOpen}
																	title={chip.isOutcome ? 'Change what happened' : 'Mark what happened'}
																	onClick={() => { setOutcomeError(''); setOpenOutcomeFor((cur) => (cur === lesson.id ? null : lesson.id)); }}>
																	{/* No leading icon on the CHIP (the choices row keeps them):
																	    measured on the built bundle, icon + "Attended" + caret is
																	    one pixel wider than the status cell with the sidebar open
																	    at 1280px, and the chip went to three lines. The tone is the
																	    meaning, the label is explicit, the caret is the affordance.
																	    NBSP glues the caret to the last word so it never wraps alone. */}
																	{chip.text}{'\u00a0'}
																	<i className="fa-sharp fa-solid fa-chevron-down today-outcome__caret" aria-hidden="true" />
																</button>
															) : canCancel ? (
																/* A later date, admins: nothing has happened to MARK, but the
																   lesson can be cancelled, so the chip opens that instead. */
																<button
																	type="button"
																	className={`ps-badge ps-badge--${timeStatus.tone} today-outcome__chip`}
																	aria-expanded={cancelOpen}
																	title="Cancel this lesson"
																	onClick={() => { setCancelError(''); setOpenCancelFor((cur) => (cur === lesson.id ? null : lesson.id)); }}>
																	{timeStatus.text}{'\u00a0'}
																	<i className="fa-sharp fa-solid fa-chevron-down today-outcome__caret" aria-hidden="true" />
																</button>
															) : (
																<span className={`ps-badge ps-badge--${isCancelled ? chip.tone : timeStatus.tone}`}>
																	{isCancelled ? chip.text : timeStatus.text}
																</span>
															)}
														</td>
														<td className="col--actions">
															<div className="d-flex gap-2">
																{lesson.studentId && (
																<button
																	type="button"
																	className="ps-btn-icon"
																	onClick={() => navigate(`/portal/work/students/${lesson.studentId}`)}
																	title="View Student Profile">
																	<i className="fa-sharp fa-solid fa-user" />
																</button>
																)}
																{!isCancelled && renderOpenButton(lesson, timeStatus, loc)}
															{/* Glance only (CSS shows it just there): open THIS lesson into
															    its full card — Start, profile, type — without switching the
															    whole list to Cards. Always a visible chevron, never a
															    tap-the-row-and-hope affordance. */}
															<button
																type="button"
																className="today-row__expand"
																aria-expanded={expandedRows.has(lesson.id)}
																aria-label={`${expandedRows.has(lesson.id) ? 'Hide' : 'Show'} details for ${lesson.studentName}`}
																onClick={() => setExpandedRows((cur) => {
																	const next = new Set(cur);
																	if (next.has(lesson.id)) next.delete(lesson.id); else next.add(lesson.id);
																	return next;
																})}>
																<i className={`fa-sharp fa-solid fa-chevron-${expandedRows.has(lesson.id) ? 'up' : 'down'}`} aria-hidden="true" />
															</button>
															</div>
														</td>
													</tr>
													{/* The choices, as a second ROW in normal flow — never a
													    positioned popover, which paints under later rows and
													    clips inside a fit-table cell (CLAUDE.md). */}
													{outcomeOpen && (
														<tr className="today-outcome__row">
															<td colSpan={5}>
																<div className="today-outcome__choices">
																	<span className="today-outcome__lead">{lesson.studentName} — what happened?</span>
																	{outcomeChoices({ isAdmin: isAdminOrOwner }).map((v) => (
																		<button
																			key={v}
																			type="button"
																			className={`today-outcome__opt${lesson.outcome === v ? ' is-current' : ''}`}
																			disabled={outcomeBusy === lesson.id || !canChangeOutcome({ from: lesson.outcome || null, to: v, isAdmin: isAdminOrOwner })}
																			onClick={() => markOutcome(lesson, v)}>
																			<i className={`fa-sharp fa-solid ${OUTCOME_META[v].icon}`} aria-hidden="true" /> {OUTCOME_META[v].label}
																		</button>
																	))}
																	{lesson.outcome && canChangeOutcome({ from: lesson.outcome, to: null, isAdmin: isAdminOrOwner }) && (
																		<button type="button" className="today-outcome__opt" disabled={outcomeBusy === lesson.id} onClick={() => markOutcome(lesson, null)}>
																			<i className="fa-sharp fa-solid fa-eraser" aria-hidden="true" /> Clear
																		</button>
																	)}
																	<button type="button" className="today-outcome__opt" onClick={() => setOpenOutcomeFor(null)}>Close</button>
																</div>
																{outcomeError && <p className="today-outcome__err">{outcomeError}</p>}
																{/* Today, before it starts: cancelling is also on offer. */}
																{canCancel && cancelChoices(lesson)}
															</td>
														</tr>
													)}
													{cancelOpen && (
														<tr className="today-outcome__row">
															<td colSpan={5}>{cancelChoices(lesson)}</td>
														</tr>
													)}
													{/* Who is teaching — the same second-row pattern as the
													    outcome choices, for the same reasons. */}
													{canReassign && !isMine && openTeacherFor === lesson.id && isReassignable(lesson) && (
														<tr className="today-outcome__row">
															<td colSpan={5}>
																<div className="today-outcome__choices">
																	<span className="today-outcome__lead">Who is teaching {lesson.studentName}?</span>
																	{activeTeachers.map((t) => (
																		<button
																			key={t.id}
																			type="button"
																			className={`today-outcome__opt${lesson.teacherId === t.id ? ' is-current' : ''}`}
																			disabled={reassignBusy}
																			onClick={() => reassignOne(lesson, t.id)}>
																			<Avatar uid={uidForTeacherId(t.id)} name={t.name} size={24} className="today-teacher__face" decorative /> {t.name}
																		</button>
																	))}
																	<button type="button" className="today-outcome__opt" onClick={() => setOpenTeacherFor(null)}>Close</button>
																</div>
																<p className="today-teacher__hint">Changes this lesson only, on this date. The teacher who gets it is notified.</p>
																{reassignError && <p className="today-outcome__err">{reassignError}</p>}
															</td>
														</tr>
													)}
													</React.Fragment>
												);
											})}
										</tbody>
									</table>
								</div>
							)}
						</div>

					</div>
				</div>

				{/* Change teachers from a time on (Anthony, 2026-10-04). Every
				    lesson that moves is listed BEFORE saving, and an end-state
				    double-booking is warned about, not blocked: the admin may
				    know something the schedule does not. */}
				{showSwap && canReassign && (
					<div className="ps-modal-overlay" onClick={() => !reassignBusy && setShowSwap(false)}>
						<div className="ps-modal today-swap" role="dialog" aria-label="Change teachers" onClick={(e) => e.stopPropagation()}>
							<h3 className="ps-modal__title">Change teachers</h3>
							<div className="ps-field">
								<label className="ps-label" htmlFor="swap-from">From</label>
								<select id="swap-from" className="ps-select" value={swapFrom} onChange={(e) => changeSwapFrom(e.target.value)}>
									{startTimes(allRows).map((t) => <option key={t} value={t}>{formatTime(t)} on</option>)}
								</select>
							</div>
							{swapLocs.length === 0 && <p className="text-muted small">No lessons from that time on.</p>}
							{swapLocs.map((l) => (
								<div className="ps-field" key={l.location}>
									<label className="ps-label" htmlFor={`swap-${l.location}`}>
										{locName(l.location)}: {l.lessons.length} lesson{l.lessons.length === 1 ? '' : 's'}, now {l.mixed ? 'more than one teacher' : teacherLabel(l.teacherId)}
									</label>
									<select id={`swap-${l.location}`} className="ps-select" value={swapTargets[l.location] || ''}
										onChange={(e) => setSwapTargets((cur) => ({ ...cur, [l.location]: e.target.value }))}>
										<option value="">Leave as is</option>
										{activeTeachers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
									</select>
								</div>
							))}
							<div className="today-swap__preview">
								<p className="ps-label">{swapMoves.length === 0 ? 'Nothing will change.' : `${swapMoves.length} lesson${swapMoves.length === 1 ? '' : 's'} will move:`}</p>
								<ul className="today-swap__list">
									{swapMoves.map((m) => (
										<li key={`${m.slotId}|${m.originDate}`}>
											<strong>{formatTime(m.row.time)}</strong> {m.row.studentName}, {locName(m.row.location)}: {teacherLabel(m.row.teacherId)} to <strong>{teacherLabel(m.teacherId)}</strong>
										</li>
									))}
								</ul>
								{swapWarnings.map((w, i) => (
									<p key={i} className="today-swap__warn" role="alert">
										<i className="fa-sharp fa-solid fa-triangle-exclamation" aria-hidden="true" /> {teacherLabel(w.teacherId)} would have two lessons at once: {formatTime(w.a.time)} {w.a.studentName} ({locName(w.a.location)}) and {formatTime(w.b.time)} {w.b.studentName} ({locName(w.b.location)}).
									</p>
								))}
							</div>
							{reassignError && <p className="today-outcome__err">{reassignError}</p>}
							<div className="ps-modal__footer">
								<button className="btn-cyv-ghost" disabled={reassignBusy} onClick={() => setShowSwap(false)}>Cancel</button>
								<button className="btn-cyv-teal" disabled={reassignBusy || swapMoves.length === 0}
									onClick={async () => { if (reassignBusy || !swapMoves.length) return; if (await saveMoves(swapMoves)) setShowSwap(false); }}>
									{reassignBusy ? 'Saving…' : `Move ${swapMoves.length} lesson${swapMoves.length === 1 ? '' : 's'}`}
								</button>
							</div>
						</div>
					</div>
				)}

				{/* Add Announcement Modal */}
				{showAnnouncementModal && canPostAnnouncement && (
					<div
						className="ps-modal-overlay"
						onClick={() => setShowAnnouncementModal(false)}>
						<div className="ps-modal" onClick={(e) => e.stopPropagation()}>
							<h3 className="ps-modal__title">New announcement</h3>
							<div className="ps-field">
								<label className="ps-label" htmlFor="ann-msg">Message</label>
								<textarea
									id="ann-msg"
									className="ps-textarea"
									rows={3}
									value={newAnnouncement}
									onChange={(e) => setNewAnnouncement(e.target.value)}
									placeholder="Share something with other teachers…"
								/>
							</div>
							<div className="ps-modal__footer">
								<button className="btn-cyv-ghost" onClick={() => setShowAnnouncementModal(false)}>
									Cancel
								</button>
								<button className="btn-cyv-teal" onClick={handleAddAnnouncement}>
									Post announcement
								</button>
							</div>
						</div>
					</div>
				)}
			</div>
		</>
	);
};

export default TodayPage;
